/**
 * Auto-add product functionality
 * Automatically adds a specific product when items from a particular collection are added to cart
 */

class CartAutoAdd {
  constructor() {
    // Configuration: Collection ID/handle mapped to product to auto-add
    this.autoAddRules = {
      '460307202270': {  // Collection ID
        collectionHandle: 'buy-any-bag-and-get-a-free-charm',  // Collection handle
        productHandle: 'cole-haan-bag-charm',  // Product handle to auto-add
        variantId: null,      // Will be fetched dynamically
        quantity: 1,
        alreadyAdded: false
      }
    };
    
    // Product to auto-add (not in the GWP collection)
    this.productHandleToAdd = 'cole-haan-bag-charm';
    this.collectionId = '460307202270';
    this.collectionHandle = 'buy-any-bag-and-get-a-free-charm';
    this.init();
  }

  init() {
    // Fetch the product to auto-add and store its variant
    this.productLoadPromise = this.loadProductToAdd();
    
    // Pre-load collection products for faster checking
    this.collectionProductsCache = null;
    this.loadCollectionProducts();
    
    // Listen for cart update events from product-form.js
    if (typeof subscribe !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'product-form' && event.productVariantId) {
          this.checkAndAutoAdd(event.productVariantId);
        }
      });
    }
  }
  
  async loadCollectionProducts() {
    try {
      const response = await fetch(`/collections/${this.collectionHandle}/products.json`);
      if (response.ok) {
        const data = await response.json();
        this.collectionProductsCache = data.products || [];
      }
    } catch (error) {
    }
  }
  
  showLoader() {
    // Create a simple loader overlay
    const loader = document.createElement('div');
    loader.id = 'auto-add-loader';
    loader.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 20px 30px;
      border-radius: 8px;
      z-index: 10000;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 12px;
    `;
    loader.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style="animation: spin 1s linear infinite;">
        <circle cx="10" cy="10" r="8" stroke="white" stroke-width="2" stroke-dasharray="50" stroke-dashoffset="25" opacity="0.3"/>
        <circle cx="10" cy="10" r="8" stroke="white" stroke-width="2" stroke-dasharray="50" stroke-linecap="round"/>
      </svg>
      <span>Adding gift to cart...</span>
      <style>
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      </style>
    `;
    document.body.appendChild(loader);
    return loader;
  }
  
  hideLoader(loader) {
    if (loader && loader.parentNode) {
      loader.remove();
    }
  }
  
  async loadProductToAdd() {
    try {
      // Fetch the product by its handle
      const response = await fetch(`/products/${this.productHandleToAdd}.js`);
      if (response.ok) {
        const product = await response.json();
        
        // Get the first variant
        if (product.variants && product.variants.length > 0) {
          const variantId = product.variants[0].id;
          // Update all rules with this variant
          Object.keys(this.autoAddRules).forEach(key => {
            this.autoAddRules[key].variantId = variantId;
            this.autoAddRules[key].productHandle = product.handle;
          });
        }
      }
    } catch (error) {
    }
  }

  async checkAndAutoAdd(addedVariantId) {
    let loader = null;
    try {
      // Wait for the product to load if it hasn't finished yet
      if (this.productLoadPromise) {
        await this.productLoadPromise;
      }
      
      // Quick check using variant data from cart
      const cartResponse = await fetch(`${window.routes.cart_url}.js`);
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const item = cart.items.find(i => i.variant_id.toString() === addedVariantId.toString());
        
        if (item) {
          // Fast check: is this product in cached collection?
          if (this.collectionProductsCache) {
            const isInCollection = this.collectionProductsCache.some(p => 
              p.handle === item.handle || p.id.toString() === item.product_id.toString()
            );
            
            if (isInCollection) {
              const collectionId = '460307202270';
              if (this.autoAddRules[collectionId] && !this.autoAddRules[collectionId].alreadyAdded) {
                const rule = this.autoAddRules[collectionId];
                
                if (rule.variantId) {
                  // Show loader
                  loader = this.showLoader();
                  
                  await this.addProductToCart(rule.variantId, rule.quantity);
                  rule.alreadyAdded = true;
                  
                  // Hide loader
                  this.hideLoader(loader);
                } else {
                  // Try loading it now
                  loader = this.showLoader();
                  await this.loadProductToAdd();
                  if (rule.variantId) {
                    await this.addProductToCart(rule.variantId, rule.quantity);
                    rule.alreadyAdded = true;
                  }
                  this.hideLoader(loader);
                }
              } else {
                console.log('Product already added');
              }
              return;
            } else {
              return;
            }
          }
        }
      }
      
      // Fallback to slower method if cart check fails
      const productHandle = await this.getProductHandleFromVariant(addedVariantId);
      if (!productHandle) {
        return;
      }
      
      const productData = await this.fetchProductData(productHandle);
      if (!productData) {
        return;
      }
      
      const isInTargetCollection = await this.checkIfProductInCollection(productData.id);
      
      if (isInTargetCollection) {
        const collectionId = '460307202270';
        if (this.autoAddRules[collectionId] && !this.autoAddRules[collectionId].alreadyAdded) {
          const rule = this.autoAddRules[collectionId];
          
          if (rule.variantId) {
            loader = this.showLoader();
            await this.addProductToCart(rule.variantId, rule.quantity);
            rule.alreadyAdded = true;
            this.hideLoader(loader);
          }
        }
      }
    } catch (error) {
      if (loader) {
        this.hideLoader(loader);
      }
    }
  }
  
  async checkIfProductInCollection(productId) {
    try {
      // Use cached collection products if available
      if (this.collectionProductsCache) {
        const isInCollection = this.collectionProductsCache.some(p => 
          p.id.toString() === productId.toString()
        );
        return isInCollection;
      }
      
      // Fallback: fetch collection products
      const response = await fetch(`/collections/${this.collectionHandle}/products.json`);
      if (response.ok) {
        const data = await response.json();
        if (data.products) {
          this.collectionProductsCache = data.products;
          const isInCollection = data.products.some(p => 
            p.id.toString() === productId.toString()
          );
          return isInCollection;
        }
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  async getProductHandleFromVariant(variantId) {
    try {
      // Try fetching variant data first
      let response = await fetch(`/variants/${variantId}.js`);
      if (response.ok) {
        const variantData = await response.json();
        
        if (variantData.handle || variantData.product_handle) {
          return variantData.handle || variantData.product_handle;
        }
        
        // If handle not in variant data, try to get it from product_id
        if (variantData.product_id) {
          const productResponse = await fetch(`/products/${variantData.product_id}.js`);
          if (productResponse.ok) {
            const productData = await productResponse.json();
            return productData.handle;
          }
        }
      }
      
      // Alternative: Search through cart to find the product
      const cartResponse = await fetch(`${window.routes.cart_url}.js`);
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const item = cart.items.find(i => i.variant_id.toString() === variantId.toString());
        if (item && item.handle) {
          return item.handle;
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  async fetchProductData(productHandle) {
    try {
      // First try the standard product endpoint
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) return null;
      
      const productData = await response.json();
      
      // If collections are not in the JSON response, we need to check the product's tags or fetch from HTML
      if (!productData.collections || productData.collections.length === 0) {
        // Try fetching the product page HTML to extract collection info
        const htmlResponse = await fetch(`/products/${productHandle}`);
        if (htmlResponse.ok) {
          const htmlText = await htmlResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlText, 'text/html');
          
          // Look for collection metadata in the page
          const collectionMeta = doc.querySelector('meta[property="product:category"]');
          
          // Alternative: check if product ID matches our known products in the collection
          // We'll use a direct ID check as a workaround
          
          // Add collection manually based on product tags or other criteria
          // Since Shopify doesn't always expose collections via API, we'll use tags
          if (productData.tags) {
            // You can check tags that indicate collection membership
          }
        }
      }
      
      return productData;
    } catch (error) {
      return null;
    }
  }

  async addProductToCart(variantId, quantity) {
    try {
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      };

      const response = await fetch('/cart/add.js', config);
      
      if (response.ok) {
        const result = await response.json();
        
        // Immediately trigger cart update
        if (typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-auto-add',
            productVariantId: variantId
          });
        }

        // Refresh cart UI immediately
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && typeof cart.renderContents === 'function') {
          // Fetch updated cart and render
          const cartData = await fetch(`${window.routes.cart_url}.js`).then(res => res.json());
        }
      }
    } catch (error) {
    }
  }

  async refreshCart(cartElement) {
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      const cartDrawer = html.querySelector('cart-drawer');
      if (cartDrawer && cartElement.tagName === 'CART-DRAWER') {
        cartElement.innerHTML = cartDrawer.innerHTML;
      }
    } catch (error) {
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartAutoAdd();
  });
} else {
  new CartAutoAdd();
}
